import { Web3Provider } from "@ethersproject/providers";
import { BigNumber, ethers } from "ethers";
import { useCallback } from "react";
import { getRpcUrls } from "../../config/chains";
import { ChainIdEnum } from "../../enums/chain";
import { UnifiedVaultAccount } from "../../types/perpetual-dex";

const getReadProviders = (
  provider: Web3Provider | undefined,
  chainId?: ChainIdEnum
): ethers.providers.Provider[] => {
  const rpcProviders = Array.from(new Set(getRpcUrls(chainId).filter(Boolean))).map(
    (rpcUrl) => new ethers.providers.JsonRpcProvider(rpcUrl)
  );

  return provider ? [...rpcProviders, provider] : rpcProviders;
};

const readWithFallback = async <T,>(
  providers: ethers.providers.Provider[],
  read: (provider: ethers.providers.Provider) => Promise<T>
): Promise<T> => {
  let lastError: unknown;

  for (const readProvider of providers) {
    try {
      return await read(readProvider);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("No provider available for unified vault read");
};

export interface UnifiedVaultReadsParams {
  vaultContract: ethers.Contract | undefined;
  account: string | null | undefined;
  usdcContract: ethers.Contract | undefined;
  provider: Web3Provider | undefined;
  chainId: ChainIdEnum | undefined;
  depositTokenIsNative: boolean;
  depositTokenIsNativeQuote: boolean;
  getAllowance: () => Promise<string>;
}

/**
 * Read-only balance/allowance queries for useUnifiedVault. Extracted verbatim
 * for file-size only.
 */
export const useUnifiedVaultReads = (params: UnifiedVaultReadsParams) => {
  const {
    vaultContract,
    account,
    usdcContract,
    provider,
    chainId,
    depositTokenIsNative,
    depositTokenIsNativeQuote,
    getAllowance,
  } = params;

  /**
   * Get account details from the vault
   */
  const getAccount = useCallback(
    async (): Promise<UnifiedVaultAccount | undefined> => {
      if (!vaultContract || !account) {
        return undefined;
      }

      try {
        const [totalBalance, availableBalance, crossMarginAllocated, isolatedAllocated] = await Promise.all([
          vaultContract.getTotalBalance(account),
          vaultContract.getAvailableBalance(account),
          vaultContract.getCrossMarginAllocated(account),
          vaultContract.getIsolatedAllocated(account),
        ]);

        return {
          totalBalance: totalBalance.toString(),
          availableBalance: availableBalance.toString(),
          crossMarginAllocated: crossMarginAllocated.toString(),
          isolatedAllocated: isolatedAllocated.toString(),
        };
      } catch (error) {
        console.error("[UnifiedVault] Error fetching account:", error);
        return undefined;
      }
    },
    [vaultContract, account]
  );

  /**
   * Get total balance in vault
   */
  const getTotalBalance = useCallback(
    async (): Promise<string> => {
      if (!vaultContract || !account) {
        return "0";
      }

      const balance = await vaultContract.getTotalBalance(account);
      return balance.toString();
    },
    [vaultContract, account]
  );

  /**
   * Get available balance (can withdraw or use for new positions)
   */
  const getAvailableBalance = useCallback(
    async (): Promise<string> => {
      if (!vaultContract || !account) {
        return "0";
      }

      const balance = await vaultContract.getAvailableBalance(account);
      return balance.toString();
    },
    [vaultContract, account]
  );

  /**
   * Get cross-margin allocated amount
   */
  const getCrossMarginAllocated = useCallback(
    async (): Promise<string> => {
      if (!vaultContract || !account) {
        return "0";
      }

      const allocated = await vaultContract.getCrossMarginAllocated(account);
      return allocated.toString();
    },
    [vaultContract, account]
  );

  /**
   * Get isolated margin allocated amount
   */
  const getIsolatedAllocated = useCallback(
    async (): Promise<string> => {
      if (!vaultContract || !account) {
        return "0";
      }

      const allocated = await vaultContract.getIsolatedAllocated(account);
      return allocated.toString();
    },
    [vaultContract, account]
  );

  /**
   * Get selected deposit token balance. On native-quote chains, include native
   * balance only when the selected token is the wrapped native quote token.
   */
  const getUSDCBalance = useCallback(
    async (): Promise<string> => {
      if (!account) {
        return "0";
      }

      const readProviders = getReadProviders(provider, chainId);

      if (depositTokenIsNative) {
        const nativeBalance = await readWithFallback<BigNumber>(
          readProviders,
          (readProvider) => readProvider.getBalance(account)
        );
        return nativeBalance.toString();
      }

      if (!usdcContract) {
        return "0";
      }

      const erc20Balance = await readWithFallback<BigNumber>(
        readProviders,
        (readProvider) =>
          new ethers.Contract(
            usdcContract.address,
            usdcContract.interface,
            readProvider
          ).balanceOf(account)
      );

      if (depositTokenIsNativeQuote) {
        const nativeBalance = await readWithFallback<BigNumber>(
          readProviders,
          (readProvider) => readProvider.getBalance(account)
        );
        return erc20Balance.add(nativeBalance).toString();
      }

      return erc20Balance.toString();
    },
    [
      account,
      chainId,
      depositTokenIsNative,
      depositTokenIsNativeQuote,
      provider,
      usdcContract,
    ]
  );

  /**
   * Check if USDC is approved for deposit target
   */
  const isApproved = useCallback(
    async (amount: string): Promise<boolean> => {
      if (depositTokenIsNative) {
        return true;
      }
      const allowance = await getAllowance();
      return BigNumber.from(allowance).gte(BigNumber.from(amount));
    },
    [depositTokenIsNative, getAllowance]
  );

  return {
    getAccount,
    getTotalBalance,
    getAvailableBalance,
    getCrossMarginAllocated,
    getIsolatedAllocated,
    getUSDCBalance,
    isApproved,
  };
};
